/* ──────────────────────────────────────────────────────────────────
   CARTERˣ Cart Drawer — vanilla custom element.
   Spec: docs/superpowers/specs/2026-05-13-cart-drawer-rebuild-design.md
   ────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const SECTION_URL = '/cart?section_id=' + SECTION_ID;

  /* ─── Global bag-icon click delegation (runs ONCE on file load) ───
     Document-level capture-phase delegation: catches clicks on ANY
     element that should open the drawer — the theme bag icon
     (#cart-icon-bubble, [data-open-cart]) AND any link/button whose
     destination is the cart page (href ending in /cart). The third-party
     AI Header block renders a plain <a href="/cart"> we don't own;
     intercepting at document level lets us hijack it without modifying
     the app's code.
     ───────────────────────────────────────────────────────────────── */
  function isCartLink(el) {
    if (!el || el.tagName !== 'A') return false;
    var href = el.getAttribute('href') || '';
    // Match /cart exactly, /cart?..., and absolute URLs to /cart
    return /(^|\/)cart(\?|$)/.test(href.replace(/https?:\/\/[^/]+/, ''));
  }
  document.addEventListener('click', function (e) {
    var target = e.target;
    if (!target || typeof target.closest !== 'function') return;
    var bag = target.closest('#cart-icon-bubble, [data-open-cart]');
    if (!bag) {
      var link = target.closest('a');
      if (!isCartLink(link)) return;
      bag = link;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    document.dispatchEvent(new CustomEvent('cartdrawer:open'));
  }, true);

  /* ─── Global fetch interceptor for third-party ATC sources ──────────
     Our own ATC paths dispatch 'cart-update' / 'cart:update' directly
     and do NOT need this interceptor:
       • cart-drawer.js postJSON (X-Cart-Drawer-Source: cart-drawer)
       • product-coordinator.js (CustomEvent 'cart:update')
       • product-box.js (CustomEvent 'cart-update')

     The interceptor exists solely to catch /cart/add POSTs from code we
     do NOT own and CANNOT modify to add explicit dispatches:
       • main.js (196KB obfuscated theme JS, audit A007/A009)
       • Simple Bundles widget (third-party)
       • Free gift snippets (third-party / Loop)
       • Loop subscription widget (third-party)
       • Any future installed app that POSTs /cart/add directly

     Hardening (2026-05-17 audit A012) — addresses the risk of a future
     third-party fetch-wrapper (Judge.me, Klaviyo, etc.) breaking ATC:
       • Idempotent — guards window.__cxCartDrawerFetchHookInstalled so
         shopify:section:load re-renders never double-wrap (which would
         cause double cart-refresh on every ATC).
       • Defensive try/catch on every observation step — if another
         wrapper later chains after ours and returns something non-
         Promise or throws, we still return the original promise and
         the caller's flow is unaffected.
       • Preserves error semantics — realFetch errors propagate
         unchanged; our .catch only swallows the OBSERVATION's error,
         not the caller's. */
  if (!window.__cxCartDrawerFetchHookInstalled) {
    const realFetch = window.fetch && window.fetch.bind(window);
    if (realFetch) {
      const isCartAdd = function (input, init) {
        try {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          const method = ((init && init.method) || (typeof input === 'object' && input && input.method) || 'GET').toUpperCase();
          return method === 'POST' && /\/cart\/add(\.js|$|\?)/.test(url);
        } catch (_) { return false; }
      };
      const isInternal = function (init) {
        try {
          const h = (init && init.headers) || {};
          if (h['X-Cart-Drawer-Source'] === 'cart-drawer') return true;
          if (typeof h.get === 'function' && h.get('X-Cart-Drawer-Source') === 'cart-drawer') return true;
        } catch (_) {}
        return false;
      };
      window.fetch = function (input, init) {
        // Always call realFetch first — preserve original error semantics
        const promise = realFetch(input, init);
        // Observation is best-effort; never let it break the fetch chain
        try {
          if (promise && typeof promise.then === 'function'
              && isCartAdd(input, init) && !isInternal(init)) {
            promise.then(function (res) {
              try {
                if (res && res.ok) {
                  document.dispatchEvent(new CustomEvent('cart-update', {
                    detail: { source: 'fetch-interceptor' }
                  }));
                }
              } catch (_) { /* swallow — never break caller */ }
            }).catch(function () { /* swallow — caller still sees rejection */ });
          }
        } catch (_) { /* never break the fetch chain */ }
        return promise;
      };
      window.__cxCartDrawerFetchHookInstalled = true;
    }
  }

  /* ─── Fetch helpers ─── */
  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Cart-Drawer-Source': 'cart-drawer'
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || ('HTTP ' + res.status));
    }
    return res.json();
  }

  async function fetchSectionDoc() {
    const res = await fetch(SECTION_URL, { headers: { 'Accept': 'text/html' } });
    if (!res.ok) throw new Error('Failed to load cart section');
    const html = await res.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  /* ─── CartDrawer custom element ─── */
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._lastFocus = null;
      this._onClick = this._onClick.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onOpenEvent = this._onOpenEvent.bind(this);
      this._onCartUpdate = this._onCartUpdate.bind(this);
      this._onQtyInput = this._onQtyInput.bind(this);
      this._onQtyChange = this._onQtyChange.bind(this);
      this._onQtyKey = this._onQtyKey.bind(this);
    }

    connectedCallback() {
      // PAGE MODE — on /cart this same element is rendered inline as the
      // cart page (see cart-drawer.liquid). It is always visible, so it must
      // never go inert, lock body scroll, trap focus or "close".
      this._page = this.hasAttribute('data-page-mode');
      // Start in inert state so the closed drawer is invisible to AT and
      // not in the keyboard tab order. open() removes inert; close() re-adds.
      if (!this._page && this.getAttribute('data-open') !== 'true') {
        this.setAttribute('inert', '');
      }
      this.addEventListener('click', this._onClick);
      this.addEventListener('submit', this._onSubmit);
      this.addEventListener('input', this._onQtyInput);
      this.addEventListener('change', this._onQtyChange);
      this.addEventListener('keydown', this._onQtyKey);
      document.addEventListener('keydown', this._onKey);
      document.addEventListener('cartdrawer:open', this._onOpenEvent);
      // Listen for every cart-change event name the theme might dispatch.
      // Dawn uses 'cart:update'; this theme (main.js) uses 'cartQuantityUpdated' + 'cart-update'.
      document.addEventListener('cart:update', this._onCartUpdate);
      document.addEventListener('cart-update', this._onCartUpdate);
      document.addEventListener('cartQuantityUpdated', this._onCartUpdate);
      // On initial load, check if the existing cart needs free gifts
      // auto-added (e.g. user landed with a SystemX bundle but no Bottle).
      setTimeout(() => {
        if (this._giftGuard) return;
        this._giftGuard = true;
        this._maybeAutoAddGifts()
          .then(added => added ? this._refresh() : null)
          .catch(() => null)
          .finally(() => { this._giftGuard = false; });
      }, 500);
      // Wire the addons swipe-arrow scroll detection on initial render
      // (section is server-rendered, JS attaches afterwards).
      this._bindAddonsSwipe();
      // Portal pages (/a/account) are SSR'd by carterx-app, which builds the
      // page from an ANONYMOUS, cookieless server-side fetch of the storefront
      // chrome (App Proxy strips the shopper's cart cookie). So the cart-drawer
      // markup that ships in that chrome is rendered with an EMPTY cart and the
      // drawer would show "CART IS EMPTY" even when the real cart has items.
      // Hydrate once on load from the storefront origin — _refresh() fetches
      // /cart?section_id=cart-drawer with the browser's real cart cookie and
      // swaps in the actual line items, count + badges. Scoped to the portal
      // path so normal pages (correct server render) take no extra fetch.
      if (location.pathname.indexOf('/a/account') === 0) {
        this._refresh();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('submit', this._onSubmit);
      this.removeEventListener('input', this._onQtyInput);
      this.removeEventListener('change', this._onQtyChange);
      this.removeEventListener('keydown', this._onQtyKey);
      document.removeEventListener('keydown', this._onKey);
      document.removeEventListener('cartdrawer:open', this._onOpenEvent);
      document.removeEventListener('cart:update', this._onCartUpdate);
      document.removeEventListener('cart-update', this._onCartUpdate);
      document.removeEventListener('cartQuantityUpdated', this._onCartUpdate);
    }

    /* ─── Open / close ─── */
    open() {
      // Page mode: already on screen. Bag-icon clicks and post-add opens
      // (_onCartUpdate) are no-ops; the refresh still runs.
      if (this._page) return;
      this._lastFocus = document.activeElement;
      this.setAttribute('data-open', 'true');
      // Removing inert here makes the drawer interactive AND visible to
      // assistive tech. While closed (inert=true) the dialog's role=dialog
      // and contents are skipped by screen readers — otherwise NVDA /
      // VoiceOver read the hidden cart on every page.
      this.removeAttribute('inert');
      // Trap focus to the close button on next tick.
      setTimeout(() => {
        const closeBtn = this.querySelector('[data-close]');
        if (closeBtn) closeBtn.focus();
      }, 0);
      document.body.style.overflow = 'hidden';
    }

    close() {
      if (this._page) return;
      this.setAttribute('data-open', 'false');
      this.setAttribute('inert', '');
      document.body.style.overflow = '';
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    _onOpenEvent() { this.open(); }

    _onKey(e) {
      if (this._page || this.getAttribute('data-open') !== 'true') return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key === 'Tab') this._trapFocus(e);
    }

    _trapFocus(e) {
      const focusable = this.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ─── Loading wrapper ─── */
    async _withLoading(fn) {
      this.setAttribute('data-loading', 'true');
      try { return await fn(); }
      finally { this.removeAttribute('data-loading'); }
    }

    /* ─── Content re-render ─── */
    async _refresh() {
      const doc = await fetchSectionDoc();
      // Refresh body (scrollable), pinned upgrade banner, pinned addons row,
      // and sticky footer independently — each lives in its own DOM
      // container inside the panel's flex column so layout stays stable.
      const refreshSelectors = ['[data-cart-header]', '[data-cart-ship]', '[data-cart-body]', '[data-cart-upgrade]', '[data-cart-addons]', '[data-cart-footer]'];
      for (const sel of refreshSelectors) {
        const source = doc.querySelector(sel);
        const target = this.querySelector(sel);
        if (source && target) target.innerHTML = source.innerHTML;
      }
      // Auto-add free gifts ONCE per refresh cycle. If anything was added,
      // re-refresh to pick up the new line item (and the auto-discount
      // makes it appear as $0).
      if (!this._giftGuard) {
        this._giftGuard = true;
        try {
          const added = await this._maybeAutoAddGifts().catch(() => false);
          if (added) {
            const doc2 = await fetchSectionDoc();
            for (const sel of refreshSelectors) {
              const source = doc2.querySelector(sel);
              const target = this.querySelector(sel);
              if (source && target) target.innerHTML = source.innerHTML;
            }
          }
        } finally {
          this._giftGuard = false;
        }
      }
      this._syncBadges().catch(() => {});
      this._bindAddonsSwipe();
      // Page mode: the empty/non-empty layout switch lives on the wrapper,
      // which refresh doesn't replace — derive it from the refreshed body.
      if (this._page) this.toggleAttribute('data-empty', !!this.querySelector('[data-cart-body] .cd-empty'));
      this._broadcast();
    }

    // Wire the addons scroll container to drive the progress indicator
    // (thin track + thumb mirroring scroll position). Re-runs on every
    // _refresh because the section innerHTML is replaced and the new
    // .cd-addons__scroll loses any prior listener. Method name retained
    // as `_bindAddonsSwipe` for git-history continuity; behaviour now
    // moves a thumb instead of fading an arrow.
    _bindAddonsSwipe() {
      const scroll = this.querySelector('.cd-addons__scroll');
      if (!scroll) return;
      const row = scroll.querySelector('.cd-addons__row');
      if (!row) return;
      const thumb = scroll.querySelector('[data-addons-progress-thumb]');

      // Sizes the thumb width (visible/total) and positions it
      // horizontally (scrollLeft/maxScroll) — both as % of the track
      // so the bar adapts to any container width without JS-measuring.
      //
      // Position uses `left` (parent-relative) rather than translateX
      // (own-width-relative). With translateX, a 30%-wide thumb at
      // full scroll would only move 70% × 30% = 21% of the track —
      // so the bar barely budged. `left` makes the math straightforward:
      // at progress=1, thumb's left edge sits at (100 - thumbWidth)%
      // of the track, so its right edge lines up with the track end.
      const update = () => {
        // Skip while the section is collapsed (display:none → 0 width).
        // Measuring here would wrongly flag "all-fits" and hide the track for
        // good, so the bar vanishes after collapse→expand. Re-measured on
        // expand (see the addons toggle handler) once the row is visible again.
        if (!row.clientWidth) return;
        const total = row.scrollWidth;
        const visible = row.clientWidth;
        const maxScroll = total - visible;
        const atEnd = row.scrollLeft + visible >= total - 2;
        // Preserve atEnd semantics — some legacy CSS may still reference it.
        scroll.setAttribute('data-at-end', atEnd ? 'true' : 'false');
        if (!thumb) return;
        // Always show the track (already gated to >1 card in Liquid). We do NOT
        // hide it when everything fits: that 'all-fits' state could get stuck
        // after a collapse→expand and make the bar vanish. A near-full-width
        // thumb when content fits is harmless.
        const ratio = Math.max(0.1, Math.min(1, visible / total));
        const widthPct = ratio * 100;
        thumb.style.width = widthPct.toFixed(2) + '%';
        const progress = maxScroll > 0 ? row.scrollLeft / maxScroll : 0;
        const leftPct = (100 - widthPct) * progress;
        thumb.style.left = leftPct.toFixed(2) + '%';
      };
      this._addonsUpdate = update;
      row.addEventListener('scroll', update, { passive: true });
      // Initial measurement + ResizeObserver so the thumb re-sizes
      // when the drawer width changes (orientation flip, font load).
      update();
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(update);
        ro.observe(row);
      }
    }

    async _maybeAutoAddGifts() {
      // Only auto-add for first-order eligibility (autoAdd: true — the
      // Liquid gate for logged-in customers). Repeat customers still have
      // rules listed in CXFreeGifts for orphan-removal detection.
      const rules = (window.CXFreeGifts || []).filter(r => r && r.trigger && r.variantId && r.autoAdd);
      if (rules.length === 0) return false;
      const cart = await fetchCart();
      const handlesInCart = new Set(cart.items.map(i => i.handle));

      // Gifts the shopper explicitly removed with the gift-row ✕ — never
      // boomerang those back. Stored as a cart attribute so it survives
      // refreshes and lands on the order (underscore = hidden at checkout).
      const declined = new Set(
        String((cart.attributes && cart.attributes._cx_declined_gifts) || '')
          .split(',').map(h => h.trim()).filter(Boolean)
      );

      // Per-gift eligibility for RECOGNIZED returning customers (CLUBˣ
      // portal session on this browser). null = anonymous/unknown →
      // optimistic auto-add; the segment-gated BXGY discounts remain the
      // price authority at checkout either way.
      const eligibility = await this._getGiftEligibility();

      const toAdd = [];
      for (const rule of rules) {
        if (!handlesInCart.has(rule.trigger)) continue;
        if (handlesInCart.has(rule.productHandle)) continue;
        if (declined.has(rule.productHandle)) continue;
        if (eligibility && eligibility[rule.productHandle] === false) continue;
        toAdd.push({ id: rule.variantId, quantity: 1 });
      }
      if (toAdd.length === 0) return false;
      try {
        await postJSON('/cart/add.js', { items: toAdd });
        return true;
      } catch (_) {
        return false;
      }
    }

    // Ask the app proxy which gifts this browser's customer is still
    // eligible for. Only fires when a CLUBˣ portal session token exists
    // (zero extra requests for anonymous shoppers). Memoized per page
    // load + cached 10 min in sessionStorage keyed to the token, so a
    // fresh login (or a new purchase) re-resolves quickly. Every storage
    // access is try/caught — storage can be blocked (private mode).
    async _getGiftEligibility() {
      if (this._giftEligPromise !== undefined) return this._giftEligPromise;
      this._giftEligPromise = (async () => {
        let token = null;
        try { token = localStorage.getItem('cx_portal_session'); } catch (_) {}
        if (!token || !window.CXGiftsEligibilityUrl) return null;
        const cacheKey = 'cx_gift_elig';
        try {
          const raw = sessionStorage.getItem(cacheKey);
          if (raw) {
            const cached = JSON.parse(raw);
            if (cached && cached.token === token && (Date.now() - cached.ts) < 600000) {
              return cached.eligibility || null;
            }
          }
        } catch (_) {}
        try {
          const res = await fetch(window.CXGiftsEligibilityUrl, {
            headers: { 'Accept': 'application/json', 'Authorization': 'Bearer ' + token }
          });
          if (!res.ok) return null;
          const data = await res.json();
          const eligibility = (data && data.ok && data.known && data.eligibility) || null;
          if (eligibility) {
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify({ token, ts: Date.now(), eligibility }));
            } catch (_) {}
          }
          return eligibility;
        } catch (_) {
          return null;
        }
      })();
      return this._giftEligPromise;
    }

    // ✕ on a nested gift row: persist the decline FIRST (so no refresh
    // cycle re-adds it), then remove the line. Order matters — removing
    // first would race _maybeAutoAddGifts back into the cart.
    async _giftRemove(btn) {
      const handle = btn.dataset.giftHandle;
      if (!handle) return;
      const row = btn.closest('.cd-item');
      this._fadeRow(row);
      await this._withLoading(async () => {
        try {
          const cart = await fetchCart();
          const declined = new Set(
            String((cart.attributes && cart.attributes._cx_declined_gifts) || '')
              .split(',').map(h => h.trim()).filter(Boolean)
          );
          declined.add(handle);
          await postJSON('/cart/update.js', {
            attributes: { _cx_declined_gifts: Array.from(declined).join(',') }
          });
          // Resolve the gift line(s) FRESH by handle rather than trusting
          // the rendered data-line-key: keys go stale whenever the cart
          // changed since render (billing toggle, a duplicate gift row
          // under a second trigger line, double-click), and a stale key
          // makes /cart/change.js throw "no valid id or line parameter".
          // Already gone → the decline attribute still lands; no error.
          const keys = cart.items.filter(i => i.handle === handle).map(i => i.key);
          if (keys.length > 0) await this._batchRemove(keys);
          await this._refresh();
        } catch (err) {
          this._restoreRow(row);
          this._showError(err && err.message ? err.message : 'Could not remove the gift.');
        }
      });
    }

    // After removing any line, scan cart for gift lines whose trigger is
    // no longer in cart. Returns the line keys to remove (may be empty).
    async _findOrphanGiftKeys() {
      const rules = window.CXFreeGifts || [];
      if (rules.length === 0) return [];
      const cart = await fetchCart();
      const handlesInCart = new Set(cart.items.map(i => i.handle));
      const orphans = [];
      for (const it of cart.items) {
        const matchingRules = rules.filter(r => r.productHandle === it.handle);
        if (matchingRules.length === 0) continue;
        // Gift is an orphan if NONE of its triggers remain in cart.
        const anyTriggerInCart = matchingRules.some(r => handlesInCart.has(r.trigger));
        if (!anyTriggerInCart) orphans.push(it.key);
      }
      return orphans;
    }

    async _syncBadges() {
      const cart = await fetchCart();
      // Strip "cart-hidden" tagged items so the visible count matches the
      // header Liquid logic (non_upsell_count) — otherwise auto-added
      // gifts/upsells would inflate the bag number.
      let count = cart.item_count;
      cart.items.forEach(it => {
        const tags = (it.product && it.product.tags) || it.product_tags || [];
        if (Array.isArray(tags) ? tags.indexOf('cart-hidden') !== -1 : false) {
          count -= (it.quantity || 1);
        }
      });
      if (count < 0) count = 0;

      // Dawn convention
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });

      // CARTERx theme header bag icon (<button id="cart-icon-bubble">):
      // Update both the "Cart (N)" text + the floating .cart-count-bubble
      // pill. Liquid only renders the bubble div when count > 0, so when
      // the cart goes 0→1 the element doesn't exist yet — create it.
      // When count → 0, remove every .cart-count-bubble inside the bag
      // button (querySelectorAll, not single bubble) in case duplicates
      // were injected by another script.
      const bagBtn = document.getElementById('cart-icon-bubble');
      if (bagBtn) {
        const txt = bagBtn.querySelector('.header__icon-text');
        if (txt) txt.textContent = 'Cart (' + count + ')';
        if (count > 0) {
          let bubble = bagBtn.querySelector('.cart-count-bubble');
          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
            bagBtn.appendChild(bubble);
          }
          const num = bubble.querySelector('span[aria-hidden]');
          if (num) num.textContent = count < 100 ? String(count) : '';
        } else {
          bagBtn.querySelectorAll('.cart-count-bubble').forEach(b => b.remove());
        }
      }

      // Fallback: any other .cart-count-bubble on the page (sticky atc, etc.)
      document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(el => { el.textContent = count; });

      // Any badge inside an <a href=*/cart*> link — catches 3rd-party blocks
      document.querySelectorAll('a[href$="/cart"], a[href*="/cart?"]').forEach(link => {
        const badge = link.querySelector('[class*="cart-badge"], [class*="cart-count"], [class*="cart_count"]');
        if (badge) badge.textContent = count > 0 ? String(count) : '';
      });
    }

    _broadcast() {
      // Notify any listener (header badge, main.js section refreshes, etc.) that
      // the cart changed. Dispatch every event name the theme might be listening
      // for. Each carries detail.source so our own _onCartUpdate ignores it.
      var detail = { source: 'cart-drawer' };
      document.dispatchEvent(new CustomEvent('cart:update',         { detail: detail }));
      document.dispatchEvent(new CustomEvent('cart-update',         { detail: detail }));
      document.dispatchEvent(new CustomEvent('cartQuantityUpdated', { detail: detail }));
    }

    /* ─── Event delegation ─── */
    _onClick(e) {
      // Close
      if (e.target.closest('[data-close]')) {
        e.preventDefault();
        this.close();
        return;
      }
      const qtyPlus = e.target.closest('[data-qty-plus]');
      if (qtyPlus) { e.preventDefault(); this._stepQty(qtyPlus, +1); return; }
      const qtyMinus = e.target.closest('[data-qty-minus]');
      if (qtyMinus) { e.preventDefault(); this._stepQty(qtyMinus, -1); return; }
      const remove = e.target.closest('[data-remove]');
      if (remove) { e.preventDefault(); this._remove(remove); return; }
      const giftRemove = e.target.closest('[data-gift-remove]');
      if (giftRemove) { e.preventDefault(); this._giftRemove(giftRemove); return; }
      const billing = e.target.closest('[data-billing-toggle]');
      if (billing) { e.preventDefault(); this._billing(billing); return; }
      const addon = e.target.closest('[data-addon-add]');
      if (addon) { e.preventDefault(); this._addon(addon); return; }
      const promoApply = e.target.closest('[data-promo-apply]');
      if (promoApply) {
        e.preventDefault();
        const form = promoApply.closest('[data-promo-form]');
        if (form) this._promoApply(form);
        return;
      }
      const promoRemove = e.target.closest('[data-promo-remove]');
      if (promoRemove) { e.preventDefault(); this._promoRemove(); return; }
      const promoToggle = e.target.closest('[data-promo-toggle]');
      if (promoToggle) {
        e.preventDefault();
        const wrap = promoToggle.closest('[data-promo-collapse]');
        if (wrap) {
          const next = wrap.getAttribute('data-expanded') !== 'true';
          wrap.setAttribute('data-expanded', String(next));
          promoToggle.setAttribute('aria-expanded', String(next));
          if (next) {
            const input = wrap.querySelector('[data-promo-input]');
            if (input && !input.disabled) setTimeout(() => input.focus(), 200);
          }
        }
        return;
      }
      // YOU MIGHT ALSO LIKE collapse — toggles a class on the
      // cx-cart-drawer host (which survives [data-cart-addons] refresh)
      // so the merchant's choice persists across cart mutations.
      const addonsToggle = e.target.closest('[data-addons-toggle]');
      if (addonsToggle) {
        e.preventDefault();
        const isOpen = !this.classList.contains('cd--addons-closed') &&
                       !(this.querySelector('[data-addons-collapse]') &&
                         this.querySelector('[data-addons-collapse]').getAttribute('data-expanded') === 'false' &&
                         !this.classList.contains('cd--addons-open'));
        if (isOpen) {
          this.classList.add('cd--addons-closed');
          this.classList.remove('cd--addons-open');
          addonsToggle.setAttribute('aria-expanded', 'false');
        } else {
          this.classList.add('cd--addons-open');
          this.classList.remove('cd--addons-closed');
          addonsToggle.setAttribute('aria-expanded', 'true');
          // Re-measure now the row is visible again so the progress bar
          // reappears + sizes correctly after a collapse→expand cycle.
          requestAnimationFrame(() => { if (this._addonsUpdate) this._addonsUpdate(); });
        }
        return;
      }
    }

    _onSubmit(e) {
      const promoForm = e.target.closest('[data-promo-form]');
      if (promoForm) { e.preventDefault(); this._promoApply(promoForm); return; }
    }

    /* ─── Cart mutations ─── */

    // Optimistic UI helper: visually fade and lock a row immediately so
    // the user gets instant feedback while the network request is in
    // flight. If the request fails, _restoreRow undoes the fade.
    _fadeRow(row) {
      if (!row) return;
      row.style.transition = 'opacity 120ms linear';
      row.style.opacity = '0.3';
      row.style.pointerEvents = 'none';
    }
    _restoreRow(row) {
      if (!row) return;
      row.style.opacity = '';
      row.style.pointerEvents = '';
    }

    // Batch-remove cart line keys in a single /cart/update.js round-trip
    // (was N sequential /cart/change.js calls — 1-3s for a SystemX Pro
    // bundle with multi-pick + 2 gifts). Returns parsed cart on success.
    async _batchRemove(keys) {
      if (!keys || keys.length === 0) return null;
      const updates = {};
      keys.forEach(k => { updates[k] = 0; });
      return postJSON('/cart/update.js', { updates });
    }

    async _qty(btn, delta) {
      const id = btn.dataset.lineKey;
      const current = parseInt(btn.dataset.qty, 10) || 1;
      const groupQty = parseInt(btn.dataset.groupQty, 10) || current;
      const row = btn.closest('.cd-item');

      // Decrement at qty 1 = remove the whole group (no separate trash btn).
      if (delta < 0 && groupQty <= 1) {
        const allKeys = (btn.dataset.allKeys || id).split(',').filter(Boolean);
        const liquidGiftKeys = (btn.dataset.giftKeys || '').split('|').filter(Boolean);
        const initial = allKeys.concat(liquidGiftKeys);
        this._fadeRow(row);
        await this._withLoading(async () => {
          try {
            await this._batchRemove(initial);
            const orphans = await this._findOrphanGiftKeys();
            if (orphans.length > 0) await this._batchRemove(orphans);
            await this._refresh();
          } catch (err) {
            this._restoreRow(row);
            throw err;
          }
        });
        return;
      }
      const next = Math.max(1, current + delta);
      this._fadeRow(row);
      await this._withLoading(async () => {
        try {
          await postJSON('/cart/change.js', { id, quantity: next });
          await this._refresh();
        } catch (err) {
          // /cart/change.js can throw 422 when increment exceeds inventory.
          // Without this handler the row fade is never reverted and the UI
          // looks frozen. Restore the row + surface a visible error.
          this._restoreRow(row);
          this._showError(err && err.message ? err.message : 'Could not update quantity.');
        }
      });
    }

    /* ─── Typeable qty (2026-10-05) ───
       The qty field is an <input type=number>. Typing and the -/+ buttons
       only change the number on screen; ONE request goes out after 500 ms
       with no further edits (or at once on Enter / blur). So tapping +
       ten times, or typing 24, costs a single /cart/change.js instead of
       one request + full refresh per click.
       Gift safety: this only ever calls the SAME mutations the old
       stepper used (/cart/change.js on the primary line, or the group
       remove path at 0, incl. nested gift keys + the orphan sweep), then
       _refresh(). _maybeAutoAddGifts and _cx_declined_gifts are untouched:
       auto-add still runs once per refresh and still skips declined gifts. */
    _qtyInputFor(btn) {
      const wrap = btn && btn.closest('.cd-qty');
      return wrap ? wrap.querySelector('[data-qty-input]') : null;
    }

    _stepQty(btn, delta) {
      const input = this._qtyInputFor(btn);
      // Older cached markup without the input: fall back to the
      // immediate per-click path.
      if (!input) return this._qty(btn, delta);
      const typed = parseInt(input.value, 10);
      const base = isNaN(typed) ? (parseInt(input.dataset.groupQty, 10) || 1) : typed;
      const next = Math.max(0, Math.min(9999, base + delta));
      input.value = String(next);
      if (next === 0) {
        // - at 1 removes the whole group straight away (as before).
        clearTimeout(input._cxQtyTimer);
        this._commitQty(input);
        return;
      }
      this._scheduleQty(input);
    }

    _scheduleQty(input) {
      clearTimeout(input._cxQtyTimer);
      input._cxQtyTimer = setTimeout(() => this._commitQty(input), 500);
    }

    _onQtyInput(e) {
      const input = e.target.closest && e.target.closest('[data-qty-input]');
      if (!input) return;
      // Empty while the shopper is mid-edit: wait for a number.
      if (input.value === '') { clearTimeout(input._cxQtyTimer); return; }
      this._scheduleQty(input);
    }

    _onQtyChange(e) {
      const input = e.target.closest && e.target.closest('[data-qty-input]');
      if (!input) return;
      clearTimeout(input._cxQtyTimer);
      this._commitQty(input);
    }

    _onQtyKey(e) {
      if (e.key !== 'Enter') return;
      const input = e.target.closest && e.target.closest('[data-qty-input]');
      if (!input) return;
      e.preventDefault();
      clearTimeout(input._cxQtyTimer);
      this._commitQty(input);
    }

    async _commitQty(input) {
      if (!input || input._cxQtyBusy) return;
      const groupQty = parseInt(input.dataset.groupQty, 10) || 1;
      const typed = parseInt(input.value, 10);
      if (isNaN(typed) || typed < 0) { input.value = String(groupQty); return; }
      const target = Math.min(9999, typed);
      if (target === groupQty) { input.value = String(groupQty); return; }
      input._cxQtyBusy = true;

      const row = input.closest('.cd-item');
      const id = input.dataset.lineKey;
      const allKeys = (input.dataset.allKeys || id || '').split(',').filter(Boolean);

      if (target === 0) {
        // Same as - at qty 1: remove every line in the group + nested
        // gifts, then sweep orphan gifts, then refresh.
        const giftKeys = (input.dataset.giftKeys || '').split('|').filter(Boolean);
        this._fadeRow(row);
        await this._withLoading(async () => {
          try {
            await this._batchRemove(allKeys.concat(giftKeys));
            const orphans = await this._findOrphanGiftKeys();
            if (orphans.length > 0) await this._batchRemove(orphans);
            await this._refresh();
          } catch (err) {
            this._restoreRow(row);
            input.value = String(groupQty);
            this._showError(err && err.message ? err.message : 'Could not update quantity.');
          } finally {
            input._cxQtyBusy = false;
          }
        });
        return;
      }

      // The row may merge several lines (same variant + plan, different
      // properties). The primary line absorbs the change, like the old
      // stepper; only if that would take it below 1 do we trim the other
      // lines too (one /cart/update.js, primary kept first).
      const primaryQty = parseInt(input.dataset.qty, 10) || groupQty;
      const others = groupQty - primaryQty;
      const nextPrimary = target - others;
      this._fadeRow(row);
      await this._withLoading(async () => {
        try {
          if (nextPrimary >= 1 || allKeys.length <= 1) {
            await postJSON('/cart/change.js', { id, quantity: Math.max(1, nextPrimary) });
          } else {
            const cart = await fetchCart();
            const lines = allKeys
              .map(k => cart.items.find(i => i.key === k))
              .filter(Boolean)
              .sort((a, b) => (a.key === id ? -1 : b.key === id ? 1 : 0));
            let remaining = target;
            const updates = {};
            lines.forEach(l => {
              const q = Math.min(l.quantity, remaining);
              updates[l.key] = q;
              remaining -= q;
            });
            await postJSON('/cart/update.js', { updates });
            const orphans = await this._findOrphanGiftKeys();
            if (orphans.length > 0) await this._batchRemove(orphans);
          }
          await this._refresh();
        } catch (err) {
          // 422 (inventory limit etc.): put the number back + say why.
          this._restoreRow(row);
          input.value = String(groupQty);
          this._showError(err && err.message ? err.message : 'Could not update quantity.');
        } finally {
          input._cxQtyBusy = false;
        }
      });
    }

    async _remove(btn) {
      // If lines are grouped (same variant + selling_plan, different
      // properties), remove ALL keys in the group — otherwise just one.
      // Also remove any nested gift line keys the Liquid render pre-
      // computed. Then defensively scan for orphan gifts (gifts whose
      // trigger is no longer in cart) in case the precomputed list was
      // stale or didn't include every parent→gift link.
      const all = (btn.dataset.allKeys || btn.dataset.lineKey || '').split(',').filter(Boolean);
      const liquidGiftKeys = (btn.dataset.giftKeys || '').split('|').filter(Boolean);
      const initial = all.concat(liquidGiftKeys);
      if (initial.length === 0) return;

      const row = btn.closest('.cd-item');
      this._fadeRow(row);

      await this._withLoading(async () => {
        try {
          await this._batchRemove(initial);
          const orphans = await this._findOrphanGiftKeys();
          if (orphans.length > 0) await this._batchRemove(orphans);
          await this._refresh();
        } catch (err) {
          this._restoreRow(row);
          throw err;
        }
      });
    }

    async _billing(btn) {
      const id = btn.dataset.lineKey;
      const planId = btn.dataset.sellingPlanId || ''; // '' = one-time
      await this._withLoading(async () => {
        try {
          await postJSON('/cart/change.js', { id, selling_plan: planId || null });
        } catch (err) {
          // Bundle products reject /cart/change.js with "Cannot apply selling
          // plan to variant". Fallback: remove the line and re-add it with the
          // selling plan (and original properties intact).
          const cart = await fetchCart();
          const line = cart.items.find(i => i.key === id);
          if (!line) throw err;
          await postJSON('/cart/change.js', { id, quantity: 0 });
          const addItem = {
            id: line.variant_id,
            quantity: line.quantity,
            properties: line.properties || {}
          };
          if (planId) addItem.selling_plan = parseInt(planId, 10);
          await postJSON('/cart/add.js', { items: [addItem] });
        }
        await this._refresh();
      });
    }

    async _addon(btn) {
      const variantId = btn.dataset.variantId;
      // Optimistic UX: visually remove the card immediately. The server
      // refresh will confirm + the card stays hidden because the product
      // is now in the cart (cart-drawer-body.liquid skips in-cart items).
      const card = btn.closest('.cd-addon');
      if (card) card.style.opacity = '0';
      await this._withLoading(async () => {
        try {
          await postJSON('/cart/add.js', { items: [{ id: parseInt(variantId, 10), quantity: 1 }] });
          await this._refresh();
        } catch (err) {
          if (card) card.style.opacity = '';
          this._showError(err.message || 'Could not add item');
        }
      });
    }

    /* ─── Promo ─── */
    async _promoApply(form) {
      const input = form.querySelector('[data-promo-input]');
      const code = (input && input.value || '').trim();
      if (!code) return;
      const errEl = this.querySelector('[data-promo-error]');
      if (errEl) errEl.textContent = '';
      await this._withLoading(async () => {
        try {
          // Set the discount cookie (Shopify follows redirects internally).
          await fetch('/discount/' + encodeURIComponent(code), { method: 'GET', redirect: 'manual' });
        } catch (_) { /* opaqueredirect is fine */ }

        const cart = await fetchCart();
        const apps = cart.cart_level_discount_applications || [];
        const codeApps = (cart.discount_applications || []).filter(a => a.type === 'discount_code');
        const matched = apps.find(a => (a.title || '').toLowerCase() === code.toLowerCase()) ||
                        codeApps.find(a => (a.title || '').toLowerCase() === code.toLowerCase());
        if (matched) {
          await this._refresh();
        } else {
          if (errEl) errEl.textContent = errEl.dataset.errorLabel || 'Invalid code';
        }
      });
    }

    async _promoRemove() {
      await this._withLoading(async () => {
        try { await fetch('/discount/', { method: 'GET', redirect: 'manual' }); }
        catch (_) { /* opaqueredirect is fine */ }
        await this._refresh();
      });
    }

    /* ─── External cart:update (from PDP add-to-cart, etc.) ─── */
    _onCartUpdate(e) {
      // Avoid recursive loop if WE dispatched the event.
      if (e && e.detail && e.detail.source === 'cart-drawer') return;
      // Refresh body and auto-open the drawer to surface the addition.
      this._refresh();
      this.open();
    }

    /* ─── Errors ─── */
    _showError(msg) {
      // Strip Shopify's "already" phrasing — "X is already sold out"
      // reads better as "X is sold out"; "maximum quantity is already
      // in your cart" reads better as "maximum quantity is in your
      // cart". Single global replace catches every variant.
      msg = String(msg).replace(/\balready\s+/gi, '');

      let el = this.querySelector('[data-error-banner]');
      if (!el) {
        el = document.createElement('div');
        el.setAttribute('data-error-banner', '');
        el.className = 'cd-error-banner';
        const panel = this.querySelector('.cd-panel');
        if (panel) panel.appendChild(el);
      }
      el.textContent = msg;
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(() => { if (el) el.remove(); }, 4000);
    }
  }

  // Element name is `cx-cart-drawer` to avoid collision with main.js's
  // legacy <cart-drawer> class (Shopify Dawn inheritance).
  if (!customElements.get('cx-cart-drawer')) {
    customElements.define('cx-cart-drawer', CartDrawer);
  }
})();
